import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Loader2, Check, AlertTriangle, ExternalLink, Calendar, Sparkles, ArrowUpRight, Shield } from "lucide-react";

const PLAN_TONE = {
  starter:    { text: "text-slate-700", bg: "bg-slate-100",  border: "border-slate-200" },
  business:   { text: "text-indigo-700",bg: "bg-indigo-50", border: "border-indigo-200" },
  enterprise: { text: "text-violet-700",bg: "bg-violet-50", border: "border-violet-200" },
};

const STATUS_LABEL = {
  active:      { label: "Active",     tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  trial:       { label: "Trial",      tone: "text-sky-700 bg-sky-50 border-sky-200" },
  expired:     { label: "Trial ended",tone: "text-rose-700 bg-rose-50 border-rose-200" },
  canceled:    { label: "Canceled",   tone: "text-slate-700 bg-slate-100 border-slate-200" },
  super_admin: { label: "Super Admin",tone: "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  } catch { return iso; }
}
function fmtMoney(cents, currency = "usd") {
  if (cents == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);
}

export default function Billing() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalBusy, setPortalBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/subscription/details");
        setData(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load billing details");
      } finally { setLoading(false); }
    })();
  }, []);

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const r = await api.post("/subscription/portal");
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal");
      setPortalBusy(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen p-10 flex items-center gap-2 text-slate-500" data-testid="billing-loading">
      <Loader2 className="w-4 h-4 animate-spin" />Loading billing…
    </div>
  );
  if (!data) return null;

  const plan = data.plan || "starter";
  const planTone = PLAN_TONE[plan] || PLAN_TONE.starter;
  const statusInfo = STATUS_LABEL[data.status] || STATUS_LABEL.trial;
  const isPaid = data.stripe_subscription_id && data.plan_status === "active";
  const hasCard = !!data.card;
  const willCancel = !!data.cancel_at_period_end;

  return (
    <div className="min-h-screen p-6 sm:p-10" data-testid="billing-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-violet-100 text-violet-600 flex items-center justify-center">
            <CreditCard className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-violet-600">// BILLING & SUBSCRIPTION</div>
            <h1 className="font-display font-black text-3xl sm:text-4xl">Your Plan</h1>
            <p className="text-sm text-slate-500 mt-1">Manage your subscription, payment method, and invoices.</p>
          </div>
        </div>

        {/* Trial banner */}
        {data.status === "trial" && (
          <div className="rounded-2xl border border-sky-200 bg-sky-50 p-5 mb-6 flex items-center gap-4" data-testid="billing-trial-banner">
            <Sparkles className="w-5 h-5 text-sky-600 shrink-0" />
            <div className="flex-1">
              <div className="font-display font-bold text-sky-900">You're on a free trial</div>
              <div className="text-sm text-sky-700">
                {data.days_left != null && data.days_left > 0
                  ? `${Math.ceil(data.days_left)} day${Math.ceil(data.days_left) === 1 ? "" : "s"} left`
                  : "Trial ending soon"} — trial ends <b>{fmtDate(data.trial_ends_at)}</b>.
              </div>
            </div>
            <Link to="/pricing" className="bf-pill inline-flex items-center gap-1" data-testid="billing-upgrade-cta">
              Upgrade <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        )}
        {data.status === "expired" && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5 mb-6 flex items-center gap-4" data-testid="billing-expired-banner">
            <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0" />
            <div className="flex-1">
              <div className="font-display font-bold text-rose-900">Trial ended</div>
              <div className="text-sm text-rose-700">Pick a plan to keep drafting proposals, running bid analyses, and using the auto-agent.</div>
            </div>
            <Link to="/pricing" className="bf-pill inline-flex items-center gap-1" data-testid="billing-choose-plan-cta">Choose a plan <ArrowUpRight className="w-4 h-4" /></Link>
          </div>
        )}
        {willCancel && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 mb-6" data-testid="billing-cancel-banner">
            <div className="font-display font-bold text-amber-900">Cancellation scheduled</div>
            <div className="text-sm text-amber-800">Your subscription will end on <b>{fmtDate(data.cancel_at || data.current_period_end)}</b>. Reactivate any time from the Stripe portal.</div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          {/* Plan tile */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="billing-plan-tile">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">CURRENT PLAN</div>
            <div className="flex items-center gap-3 mb-4">
              <div className={`px-3 py-1 rounded-lg border font-display font-bold text-lg capitalize ${planTone.text} ${planTone.bg} ${planTone.border}`} data-testid="billing-plan-name">{plan}</div>
              <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border ${statusInfo.tone}`} data-testid="billing-status-badge">{statusInfo.label}</span>
            </div>
            {isPaid && data.current_period_end && (
              <div className="text-sm text-slate-700 flex items-center gap-2 mb-1" data-testid="billing-renewal">
                <Calendar className="w-4 h-4 text-slate-400" />
                {willCancel ? "Ends" : "Renews"} <b className="ml-1">{fmtDate(data.current_period_end)}</b>
              </div>
            )}
            {isPaid && data.next_invoice_amount_cents != null && !willCancel && (
              <div className="text-sm text-slate-700 flex items-center gap-2" data-testid="billing-next-invoice">
                <span className="text-slate-400">Next invoice:</span>
                <b>{fmtMoney(data.next_invoice_amount_cents, data.currency)}</b>
                {data.interval && <span className="text-slate-500">/ {data.interval}</span>}
              </div>
            )}
            {(data.extra_seats || 0) > 0 && (
              <div className="text-xs text-slate-500 mt-2">+{data.extra_seats} extra seat{data.extra_seats === 1 ? "" : "s"}</div>
            )}
            <div className="mt-5 flex gap-2 flex-wrap">
              <Link to="/pricing" className="text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 inline-flex items-center gap-1" data-testid="billing-change-plan">
                {isPaid ? "Change plan" : "See plans"} <ArrowUpRight className="w-3 h-3" />
              </Link>
              {isPaid && (
                <button
                  onClick={openPortal}
                  disabled={portalBusy}
                  className="text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid="billing-manage-in-stripe"
                >
                  {portalBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />}
                  Manage in Stripe
                </button>
              )}
            </div>
          </div>

          {/* Payment method tile */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="billing-card-tile">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">PAYMENT METHOD</div>
            {hasCard ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-9 rounded-md bg-gradient-to-br from-slate-800 to-slate-950 flex items-center justify-center text-white font-display text-[10px] uppercase tracking-widest">{data.card.brand}</div>
                  <div>
                    <div className="font-display font-bold text-lg" data-testid="billing-card-last4">•••• •••• •••• {data.card.last4}</div>
                    <div className="text-xs text-slate-500">Expires {String(data.card.exp_month).padStart(2, "0")}/{String(data.card.exp_year).slice(-2)}</div>
                  </div>
                </div>
                <button
                  onClick={openPortal}
                  disabled={portalBusy}
                  className="text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid="billing-update-card"
                >
                  {portalBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CreditCard className="w-3 h-3" />}
                  Update card
                </button>
              </>
            ) : isPaid ? (
              <div className="text-sm text-slate-500" data-testid="billing-card-missing">No card details available — <button className="text-indigo-600 underline" onClick={openPortal}>open Stripe</button> to add one.</div>
            ) : (
              <div className="text-sm text-slate-500" data-testid="billing-card-none">No payment method on file yet. It will be collected when you <Link to="/pricing" className="text-indigo-600 underline">choose a plan</Link>.</div>
            )}
          </div>

          {/* Workspace owner */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="billing-owner-tile">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">WORKSPACE OWNER</div>
            <div className="font-display font-bold text-base">{data.workspace_owner?.name || "—"}</div>
            <div className="text-sm text-slate-500">{data.workspace_owner?.email}</div>
            <div className="text-xs text-slate-400 mt-2">Member since {fmtDate(data.created_at)}</div>
          </div>

          {/* Invoices & receipts */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="billing-invoices-tile">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">INVOICES & RECEIPTS</div>
            <p className="text-sm text-slate-500 mb-4">Every invoice and receipt lives inside the Stripe customer portal — download PDFs, update tax info, or cancel your plan there.</p>
            {isPaid ? (
              <button
                onClick={openPortal}
                disabled={portalBusy}
                className="bf-pill inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="billing-invoices-portal"
              >
                {portalBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                Open billing portal
              </button>
            ) : (
              <div className="text-xs text-slate-400 flex items-center gap-1"><Shield className="w-3 h-3" />Available after your first paid invoice</div>
            )}
          </div>
        </div>

        {data.stripe_error && (
          <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700 flex items-center gap-2" data-testid="billing-stripe-warning">
            <AlertTriangle className="w-4 h-4" /> {data.stripe_error}
          </div>
        )}
      </div>
    </div>
  );
}
